import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/lib/utils';
import {
    LayoutDashboard, Users, Package, AlertTriangle,
    Activity, Settings, BarChart3, Palette, MessageCircle, FolderTree, Wallet, Flag, Megaphone, ShoppingBag,
    ChevronDown, Menu, X, Eye, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// Import refactored components
import {
    AdminDashboardTab,
    AdminUsersTab,
    AdminDisputesTab,
    AdminOrdersTab,
    AdminLogsTab,
    AdminReportsTab,
    AdminConversationsTab,
    AdminListingsTab
} from '@/components/admin';
import AdminAspectTab from '@/components/admin/AdminAspectTab';
import AdminCategoriesTab from '@/components/admin/AdminCategoriesTab';
import AdminEscrowTab from '@/components/admin/AdminEscrowTab';
import AdminUserReportsTab from '@/components/admin/AdminUserReportsTab';
import AdminAnnouncementsTab from '@/components/admin/AdminAnnouncementsTab';
import AdminVisitorsTab from '@/components/admin/AdminVisitorsTab';
import AdminModerationTab from '@/components/admin/AdminModerationTab';

const AdminPage = () => {
    const { user, isAuthenticated, isLoggingOut } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Determine default tab based on role
    const getDefaultTab = () => {
        if (user?.role === 'admin') return 'dashboard';
        return 'users'; // Default for moderator
    };
    
    // Active tab state - read from URL or default based on role
    const urlTab = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(urlTab || getDefaultTab());
    
    // Sync activeTab with URL when searchParams change
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams]);

    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState(null);
    const [users, setUsers] = useState([]);
    const [disputes, setDisputes] = useState([]);
    const [logs, setLogs] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [totals, setTotals] = useState({});
    const [openReportsCount, setOpenReportsCount] = useState(0);
    const [pendingModerationCount, setPendingModerationCount] = useState(0);

    // Dialogs
    const [banDialog, setBanDialog] = useState({ open: false, user: null });
    const [roleDialog, setRoleDialog] = useState({ open: false, user: null, newRole: 'user' });
    const [disputeDialog, setDisputeDialog] = useState({ open: false, dispute: null });
    const [escrowDialog, setEscrowDialog] = useState({ open: false, order: null, action: '' });
    const [investigateDialog, setInvestigateDialog] = useState({ open: false, data: null });

    // Log filters
    const [logFilters, setLogFilters] = useState({ username: '', ip: '', action: '' });
    const [logUsers, setLogUsers] = useState([]);
    const [logIps, setLogIps] = useState([]);
    const [logActions, setLogActions] = useState([]);

    // Chart period
    const [chartPeriod, setChartPeriod] = useState('30d');
    
    // Mobile menu state
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Listen for toggle event from Navbar
    useEffect(() => {
        const handleToggle = () => {
            setMobileMenuOpen(prev => !prev);
        };
        window.addEventListener('toggleAdminSidebar', handleToggle);
        return () => window.removeEventListener('toggleAdminSidebar', handleToggle);
    }, []);
    
    // Listen for tab change events from AdminFloatingMenu
    useEffect(() => {
        const handleTabChange = (e) => {
            const { tab } = e.detail;
            if (tab) {
                setActiveTab(tab);
                setSearchParams({ tab });
            }
        };
        window.addEventListener('adminTabChange', handleTabChange);
        return () => window.removeEventListener('adminTabChange', handleTabChange);
    }, [setSearchParams]);
    
    // Handle tab change from dashboard cards
    const [listingsFilter, setListingsFilter] = useState({ type: null, status: null });
    const [ordersFilter, setOrdersFilter] = useState({ status: null });
    
    const handleNavigateToTab = (tab, filters = {}) => {
        setActiveTab(tab);
        setSearchParams({ tab });
        
        // Set filters based on the tab
        if (tab === 'listings' && filters) {
            setListingsFilter({ type: filters.type || null, status: filters.status || null });
        }
        if (tab === 'orders' && filters) {
            setOrdersFilter({ status: filters.status || null });
        }
    };

    useEffect(() => {
        // Don't redirect during logout (which will navigate to homepage)
        if (!isAuthenticated && !isLoggingOut) {
            navigate('/login');
            return;
        }
        if (!['admin', 'moderator'].includes(user?.role)) {
            toast.error('Acces interzis');
            navigate('/');
            return;
        }
        
        // Validate tab access for moderators
        const moderatorTabs = ['users', 'disputes', 'orders', 'user-reports', 'logs'];
        if (user?.role === 'moderator' && !moderatorTabs.includes(activeTab)) {
            setActiveTab('users');
            setSearchParams({ tab: 'users' });
        }
        
        fetchData();
    }, [isAuthenticated, isLoggingOut, user, navigate]);

    useEffect(() => {
        if (dashboard) {
            fetchDashboardData();
        }
    }, [chartPeriod]);

    const fetchDashboardData = async () => {
        if (user?.role !== 'admin') return; // Only admins can fetch dashboard data
        try {
            const dashRes = await api.get(`/admin/dashboard?period=${chartPeriod}`);
            setDashboard(dashRes.data);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (logFilters.username) params.append('username', logFilters.username);
            if (logFilters.ip) params.append('ip', logFilters.ip);
            if (logFilters.action) params.append('action', logFilters.action);

            // Base requests that both admin and moderator can access
            const baseRequests = [
                api.get('/admin/users'),
                api.get('/admin/disputes'),
                api.get(`/admin/activity-logs?limit=100${params.toString() ? '&' + params.toString() : ''}`),
                api.get('/admin/activity-logs/users'),
                api.get('/admin/activity-logs/ips'),
                api.get('/admin/activity-logs/actions'),
                api.get('/reports'),
                api.get('/admin/moderation/count').catch(() => ({ data: { pending_count: 0 } })),
            ];

            // Add dashboard request only for admins
            const isAdmin = user?.role === 'admin';
            const requests = isAdmin 
                ? [api.get(`/admin/dashboard?period=${chartPeriod}`), ...baseRequests]
                : baseRequests;

            const results = await Promise.all(requests);
            
            let dashRes, usersRes, disputesRes, logsRes, logUsersRes, logIpsRes, logActionsRes, reportsRes, moderationRes;
            
            if (isAdmin) {
                [dashRes, usersRes, disputesRes, logsRes, logUsersRes, logIpsRes, logActionsRes, reportsRes, moderationRes] = results;
                setDashboard(dashRes.data);
                const totalsData = dashRes.data.totals || {};
                setTotals({
                    total_users: totalsData.total_users || 0,
                    total_listings: totalsData.total_listings || 0,
                    active_auctions: totalsData.active_auctions || 0,
                    active_sales: totalsData.active_sales || 0,
                    sold_auctions: totalsData.sold_auctions || 0,
                    sold_sales: totalsData.sold_sales || 0,
                    pending_disputes: totalsData.pending_disputes || (disputesRes.data?.disputes || disputesRes.data || []).filter(d => d.status === 'pending').length,
                    total_money_in: totalsData.total_money_in || 0,
                    total_money_out: totalsData.total_money_out || 0,
                    total_profit: totalsData.total_profit || 0,
                    held_in_escrow: totalsData.held_in_escrow || 0,
                    active_auctions_value: totalsData.active_auctions_value || 0,
                    active_sales_value: totalsData.active_sales_value || 0,
                    completed_auctions_value: totalsData.completed_auctions_value || 0,
                    completed_sales_value: totalsData.completed_sales_value || 0,
                    cancelled_orders_value: totalsData.cancelled_orders_value || 0,
                    cancelled_orders_count: totalsData.cancelled_orders_count || 0,
                    finished_auctions_count: totalsData.finished_auctions_count || 0,
                    finished_sales_count: totalsData.finished_sales_count || 0,
                    open_orders: totalsData.open_orders || 0,
                    open_auctions_count: totalsData.open_auctions_count || 0,
                    open_sales_count: totalsData.open_sales_count || 0,
                    // Reports counts
                    total_reports: totalsData.total_reports || 0,
                    pending_reports: totalsData.pending_reports || 0,
                    listing_reports: totalsData.listing_reports || 0,
                    user_reports: totalsData.user_reports || 0,
                    // Today's visitors
                    today_unique_visitors: totalsData.today_unique_visitors || 0,
                    today_visits: totalsData.today_visits || 0
                });
            } else {
                [usersRes, disputesRes, logsRes, logUsersRes, logIpsRes, logActionsRes, reportsRes, moderationRes] = results;
                // Set basic totals for moderator
                const disputesList = disputesRes.data?.disputes || disputesRes.data || [];
                setTotals({
                    pending_disputes: disputesList.filter(d => d.status === 'pending').length
                });
            }

            setUsers(usersRes.data?.users || usersRes.data || []);
            setDisputes(disputesRes.data?.disputes || disputesRes.data || []);
            setLogs(logsRes.data?.logs || []);
            setLogUsers(logUsersRes.data?.users || logUsersRes.data || []);
            setLogIps(logIpsRes.data?.ips || logIpsRes.data || []);
            setLogActions(logActionsRes.data?.actions || logActionsRes.data || []);
            
            // Count open reports (pending + investigating)
            const allReports = reportsRes.data || [];
            const openCount = allReports.filter(r => r.status === 'pending' || r.status === 'investigating').length;
            setOpenReportsCount(openCount);
            
            // Set pending moderation count
            setPendingModerationCount(moderationRes?.data?.pending_count || 0);

        } catch (error) {
            console.error('Error fetching admin data:', error);
            toast.error('Eroare la încărcarea datelor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!loading) {
            fetchData();
        }
    }, [logFilters]);

    // Handlers
    const handleInvestigate = async (userId) => {
        try {
            const res = await api.get(`/admin/users/${userId}/investigate`);
            setInvestigateDialog({ open: true, data: res.data });
        } catch (error) {
            toast.error('Eroare la investigare');
        }
    };

    const handleBanUser = async () => {
        if (!banDialog.user) return;
        try {
            await api.post(`/admin/users/${banDialog.user.user_id}/ban`);
            toast.success('Utilizator blocat');
            setBanDialog({ open: false, user: null });
            fetchData();
        } catch (error) {
            toast.error('Eroare la blocare');
        }
    };

    const handleUnbanUser = async (userId) => {
        try {
            await api.post(`/admin/users/${userId}/unban`);
            toast.success('Utilizator deblocat');
            fetchData();
        } catch (error) {
            toast.error('Eroare la deblocare');
        }
    };

    const handleChangeRole = async () => {
        if (!roleDialog.user) return;
        try {
            await api.put(`/admin/users/${roleDialog.user.user_id}`, { role: roleDialog.newRole });
            toast.success('Rol actualizat');
            setRoleDialog({ open: false, user: null, newRole: 'user' });
            fetchData();
        } catch (error) {
            toast.error('Eroare la schimbarea rolului');
        }
    };

    const handleResolveDispute = async () => {
        if (!disputeDialog.dispute) return;
        try {
            await api.post(`/admin/disputes/${disputeDialog.dispute.report_id}/resolve`);
            toast.success('Dispută rezolvată');
            setDisputeDialog({ open: false, dispute: null });
            fetchData();
        } catch (error) {
            toast.error('Eroare la rezolvarea disputei');
        }
    };

    const handleEscrowAction = async () => {
        if (!escrowDialog.order) return;
        try {
            await api.post(`/admin/orders/${escrowDialog.order.order_id}/escrow`, {
                action: escrowDialog.action
            });
            toast.success(`Acțiune escrow: ${escrowDialog.action}`);
            setEscrowDialog({ open: false, order: null, action: '' });
            fetchData();
        } catch (error) {
            toast.error('Eroare la acțiunea escrow');
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto py-8 px-4">
                <Skeleton className="h-8 w-48 mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-24" />
                    ))}
                </div>
            </div>
        );
    }

    const sidebarItems = [
        // Admin-only items first
        ...(user?.role === 'admin' ? [
            { value: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { value: 'aspect', icon: Palette, label: 'Generale' },
            { value: 'reports', icon: BarChart3, label: 'Rapoarte' },
            { value: 'listings', icon: ShoppingBag, label: 'Listări' },
            { value: 'orders', icon: Package, label: 'Vândute' },
        ] : []),
        // Moderator + Admin items
        { value: 'moderation', icon: ShieldCheck, label: 'Moderare', count: pendingModerationCount > 0 ? pendingModerationCount : null },
        { value: 'users', icon: Users, label: 'Utilizatori', count: totals.total_users },
        { value: 'disputes', icon: AlertTriangle, label: 'Dispute', count: totals.pending_disputes },
        ...(user?.role !== 'admin' ? [{ value: 'orders', icon: Package, label: 'Vândute' }] : []),
        { value: 'user-reports', icon: Flag, label: 'Raportări', count: openReportsCount > 0 ? openReportsCount : null },
        { value: 'logs', icon: Activity, label: 'Jurnal' },
        // Remaining Admin-only items
        ...(user?.role === 'admin' ? [
            { value: 'conversations', icon: MessageCircle, label: 'Conversații' },
            { value: 'visitors', icon: Eye, label: 'Vizitatori' },
            { value: 'categories', icon: FolderTree, label: 'Categorii' },
            { value: 'escrow', icon: Wallet, label: 'Escrow' },
            { value: 'announcements', icon: Megaphone, label: 'Anunțuri' },
        ] : []),
    ];

    return (
        <div className="min-h-screen flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 flex-col fixed left-0 top-[64px] bottom-0 bg-gray-50 border-r overflow-y-auto">
                {/* Role Badge */}
                <div className="p-4 border-b bg-white">
                    <Badge className={user?.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                        {user?.role === 'admin' ? 'Administrator' : 'Moderator'}
                    </Badge>
                </div>
                
                {/* Navigation Items */}
                <nav className="flex-1 p-2">
                    <div className="space-y-1">
                        {sidebarItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeTab === item.value;
                            return (
                                <button
                                    key={item.value}
                                    onClick={() => handleNavigateToTab(item.value)}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        isActive 
                                            ? 'bg-primary text-primary-foreground' 
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <Icon className="w-5 h-5 shrink-0" />
                                    <span className="flex-1 text-left">{item.label}</span>
                                    {item.count !== undefined && item.count !== null && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            isActive 
                                                ? 'bg-primary-foreground/20 text-primary-foreground' 
                                                : 'bg-gray-200 text-gray-600'
                                        }`}>
                                            {item.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </nav>
            </aside>

            {/* Mobile Sidebar - slides from left */}
            <div className="md:hidden">
                {/* Overlay */}
                {mobileMenuOpen && (
                    <div 
                        className="fixed inset-0 bg-black/50 z-50"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                )}

                {/* Sliding Sidebar */}
                <aside 
                    className={`fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-xl transform transition-transform duration-300 ease-in-out ${
                        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                >
                    {/* Sidebar Header */}
                    <div className="h-[64px] px-4 flex items-center justify-between border-b">
                        <h2 className="font-semibold text-lg">Administrare</h2>
                        <button 
                            onClick={() => setMobileMenuOpen(false)}
                            className="p-2 rounded-lg hover:bg-gray-100"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Navigation Items */}
                    <nav className="flex-1 p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
                        <div className="space-y-1">
                            {sidebarItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = activeTab === item.value;
                                return (
                                    <button
                                        key={item.value}
                                        onClick={() => {
                                            handleNavigateToTab(item.value);
                                            setMobileMenuOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                                            isActive 
                                                ? 'bg-primary text-primary-foreground' 
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <Icon className="w-5 h-5 shrink-0" />
                                        <span className="flex-1 text-left">{item.label}</span>
                                        {item.count !== undefined && item.count !== null && (
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                isActive 
                                                    ? 'bg-primary-foreground/20 text-primary-foreground' 
                                                    : 'bg-gray-200 text-gray-600'
                                            }`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </nav>
                </aside>
            </div>

            {/* Content Area */}
            <main className="flex-1 md:ml-64 min-h-screen overflow-x-hidden">
                <div className="p-4 md:p-6 space-y-6 max-w-full overflow-x-hidden">
                    {activeTab === 'dashboard' && (
                        <AdminDashboardTab
                            totals={totals}
                            dashboard={dashboard}
                            chartPeriod={chartPeriod}
                            setChartPeriod={setChartPeriod}
                            onNavigate={handleNavigateToTab}
                        />
                    )}
                    {activeTab === 'reports' && <AdminReportsTab totals={totals} />}
                    {activeTab === 'listings' && <AdminListingsTab initialFilter={listingsFilter} />}
                    {activeTab === 'moderation' && <AdminModerationTab />}
                    {activeTab === 'users' && (
                        <AdminUsersTab
                            currentUserRole={user?.role}
                            onInvestigate={handleInvestigate}
                            onSetRoleDialog={setRoleDialog}
                            onSetBanDialog={setBanDialog}
                            onUnbanUser={handleUnbanUser}
                            onRefreshUsers={fetchData}
                        />
                    )}
                    {activeTab === 'disputes' && <AdminDisputesTab onRefresh={fetchData} />}
                    {activeTab === 'orders' && <AdminOrdersTab onSetEscrowDialog={setEscrowDialog} onRefresh={fetchData} initialFilter={ordersFilter} />}
                    {activeTab === 'logs' && (
                        <AdminLogsTab
                            logs={logs}
                            logFilters={logFilters}
                            setLogFilters={setLogFilters}
                            logUsers={logUsers}
                            logIps={logIps}
                            logActions={logActions}
                            onRefresh={fetchData}
                        />
                    )}
                    {activeTab === 'conversations' && <AdminConversationsTab />}
                    {activeTab === 'visitors' && <AdminVisitorsTab />}
                    {activeTab === 'user-reports' && <AdminUserReportsTab />}
                    {activeTab === 'aspect' && <AdminAspectTab />}
                    {activeTab === 'categories' && <AdminCategoriesTab />}
                    {activeTab === 'escrow' && <AdminEscrowTab />}
                    {activeTab === 'announcements' && <AdminAnnouncementsTab />}
                </div>
            </main>

            {/* Ban Dialog */}
            <Dialog open={banDialog.open} onOpenChange={(o) => setBanDialog({ ...banDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Blochează utilizator</DialogTitle>
                    </DialogHeader>
                    <p>Sigur vrei să blochezi utilizatorul <strong>{banDialog.user?.username}</strong>?</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBanDialog({ open: false, user: null })}>
                            Anulează
                        </Button>
                        <Button variant="destructive" onClick={handleBanUser}>
                            Blochează
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Role Dialog */}
            <Dialog open={roleDialog.open} onOpenChange={(o) => setRoleDialog({ ...roleDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Schimbă rolul utilizatorului</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p>Utilizator: <strong>{roleDialog.user?.username}</strong></p>
                        <Select value={roleDialog.newRole} onValueChange={(v) => setRoleDialog({ ...roleDialog, newRole: v })}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="user">Utilizator</SelectItem>
                                <SelectItem value="moderator">Moderator</SelectItem>
                                <SelectItem value="admin">Administrator</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRoleDialog({ open: false, user: null, newRole: 'user' })}>
                            Anulează
                        </Button>
                        <Button onClick={handleChangeRole}>
                            Salvează
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dispute Dialog */}
            <Dialog open={disputeDialog.open} onOpenChange={(o) => setDisputeDialog({ ...disputeDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rezolvă disputa</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <p><strong>Motiv:</strong> {disputeDialog.dispute?.reason}</p>
                        <p><strong>Descriere:</strong> {disputeDialog.dispute?.description}</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDisputeDialog({ open: false, dispute: null })}>
                            Anulează
                        </Button>
                        <Button onClick={handleResolveDispute}>
                            Marchează rezolvat
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Escrow Dialog */}
            <Dialog open={escrowDialog.open} onOpenChange={(o) => setEscrowDialog({ ...escrowDialog, open: o })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {escrowDialog.action === 'release' && 'Eliberează fondurile'}
                            {escrowDialog.action === 'block' && 'Blochează fondurile'}
                            {escrowDialog.action === 'refund' && 'Rambursează fondurile'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <p><strong>Comandă:</strong> #{escrowDialog.order?.order_id.slice(-8)}</p>
                        <p><strong>Sumă:</strong> {formatCurrency(escrowDialog.order?.amount)}</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEscrowDialog({ open: false, order: null, action: '' })}>
                            Anulează
                        </Button>
                        <Button
                            variant={escrowDialog.action === 'refund' ? 'destructive' : 'default'}
                            onClick={handleEscrowAction}
                        >
                            Confirmă
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Investigate Dialog */}
            <Dialog open={investigateDialog.open} onOpenChange={(o) => setInvestigateDialog({ ...investigateDialog, open: o })}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Investigație utilizator</DialogTitle>
                    </DialogHeader>
                    {investigateDialog.data && (
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold mb-2">Informații generale</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <p><strong>Username:</strong> {investigateDialog.data.user?.username}</p>
                                    <p><strong>Email:</strong> {investigateDialog.data.user?.email}</p>
                                    <p><strong>Rol:</strong> {investigateDialog.data.user?.role}</p>
                                    <p><strong>Rating:</strong> {investigateDialog.data.user?.rating?.toFixed(1) || 'N/A'}</p>
                                </div>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-2">Statistici</h4>
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                    <p><strong>Listări:</strong> {investigateDialog.data.stats?.listings || 0}</p>
                                    <p><strong>Comenzi:</strong> {investigateDialog.data.stats?.orders || 0}</p>
                                    <p><strong>Dispute:</strong> {investigateDialog.data.stats?.disputes || 0}</p>
                                </div>
                            </div>
                            {investigateDialog.data.reports?.about_user?.length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2">Rapoarte despre utilizator</h4>
                                    <div className="space-y-2">
                                        {investigateDialog.data.reports.about_user.map((r, i) => (
                                            <div key={i} className="p-2 bg-muted/30 rounded text-sm">
                                                <p><strong>{r.reason}</strong></p>
                                                <p className="text-muted-foreground">{r.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPage;
